import { useState } from 'react';
import { useDatos } from '../datos/DatosContext';
import { useAviso, useRetardo } from '../lib/avisos';
import { formatearFecha, isoHace, nuevoId, ultimosSieteDias } from '../lib/fechas';
import { observarPatrones } from '../datos/simulaciones';
import { Icono } from '../componentes/ui/Icono';
import { Cabecera, Demo, GrupoChips, Pensando, TituloSeccion } from '../componentes/ui/basicos';

type EstadoPatrones = 'inactivo' | 'cargando' | 'listo';

export function Diario() {
  const { datos, agregar, actualizar } = useDatos();
  const aviso = useAviso();
  const retardo = useRetardo();
  const [emociones, setEmociones] = useState<string[]>([]);
  const [intensidad, setIntensidad] = useState(0);
  const [intencion, setIntencion] = useState('');
  const [reflexion, setReflexion] = useState('');
  const [necesito, setNecesito] = useState('');
  const [masCampos, setMasCampos] = useState(false);
  const [patrones, setPatrones] = useState<EstadoPatrones>('inactivo');

  const guardar = () => {
    if (!emociones.length && !intencion.trim()) { aviso('Elige al menos una emoción o escribe una intención.'); return; }
    agregar('diario', { id: nuevoId('d'), fecha: isoHace(0), emociones, intensidad, intencion: intencion.trim(), reflexion: reflexion.trim(), queNecesito: necesito.trim(), permitirIA: false });
    setEmociones([]); setIntensidad(0); setIntencion(''); setReflexion(''); setNecesito(''); setMasCampos(false); setPatrones('inactivo');
    aviso('Registro guardado (solo en esta sesión). Por defecto, sin permiso de IA.');
  };

  const semana = ultimosSieteDias().map((d) => {
    const registros = datos.diario.filter((r) => r.fecha === d.iso);
    const max = registros.reduce((m, r) => Math.max(m, r.intensidad || 0), 0);
    return {
      ...d,
      clase: registros.length ? (max >= 4 ? 'dot hi' : 'dot has') : 'dot',
      descripcion: formatearFecha(d.iso) + ': ' + (registros.length ? registros.length + (registros.length === 1 ? ' registro' : ' registros') : 'sin registros')
    };
  });

  return (
    <div className="stack-lg">
      <Cabecera antetitulo="Hacer una pausa" titulo="¿Cómo te sientes?" subtitulo="Elegir una emoción ya es un registro completo." />
      <div className="card stack">
        <GrupoChips etiqueta="Emociones" opciones={datos.opciones.emocionesDiario} seleccion={emociones}
          onElegir={(e) => setEmociones((l) => (l.includes(e) ? l.filter((x) => x !== e) : [...l, e]))} />
        <div className="stack-sm">
          <span className="lbl">Intensidad (opcional)</span>
          <div className="lvls" role="group" aria-label="Intensidad de 1 a 5">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} className={intensidad === n ? 'lvl on' : 'lvl'} aria-pressed={intensidad === n} onClick={() => setIntensidad((v) => (v === n ? 0 : n))}>{n}</button>
            ))}
          </div>
        </div>
        <div className="field">
          <label htmlFor="d-int">Mi intención (opcional)</label>
          <input id="d-int" type="text" placeholder="Hoy quiero…" value={intencion} onChange={(e) => setIntencion(e.target.value)} />
        </div>
        {masCampos ? (
          <>
            <div className="field"><label htmlFor="d-refl">Reflexión libre</label><textarea id="d-refl" rows={3} value={reflexion} onChange={(e) => setReflexion(e.target.value)} /></div>
            <div className="field"><label htmlFor="d-nec">Qué necesito</label><input id="d-nec" type="text" value={necesito} onChange={(e) => setNecesito(e.target.value)} /></div>
          </>
        ) : null}
        <div className="between">
          <button className="btn quiet" onClick={() => setMasCampos((v) => !v)}>{masCampos ? 'Menos campos' : 'Añadir reflexión o necesidad'}</button>
          <button className="btn primary" onClick={guardar}>Guardar registro</button>
        </div>
      </div>

      <section className="card stack-sm">
        <div className="between"><h2 className="eyebrow">Últimos 7 días</h2><span className="muted small">En rosa: intensidad 4 o más</span></div>
        <div className="week">
          {semana.map((d) => (
            <div key={d.iso} className="day"><span>{d.letra}</span><span className={d.clase} role="img" aria-label={d.descripcion} /><span>{d.numero}</span></div>
          ))}
        </div>
      </section>

      <section className="stack-sm">
        <div className="between"><TituloSeccion>Observar patrones</TituloSeccion><Demo>Simulado</Demo></div>
        {patrones === 'inactivo' ? (
          <div className="row">
            <button className="btn" onClick={() => { setPatrones('cargando'); retardo(() => setPatrones('listo'), 1300); }}>
              <Icono nombre="spark" tam="sm" />Ver patrones de esta semana
            </button>
            <span className="muted small">Solo usa registros con permiso de IA.</span>
          </div>
        ) : null}
        {patrones === 'cargando' ? <Pensando>Mirando tus registros autorizados…</Pensando> : null}
        {patrones === 'listo' ? (
          <div className="draft stack-sm">
            <span className="tag dusk">Observación · no es un diagnóstico</span>
            <p className="prose" style={{ margin: 0 }}>{observarPatrones(datos.diario)}</p>
            <div><button className="btn quiet" onClick={() => setPatrones('inactivo')}>Cerrar</button></div>
          </div>
        ) : null}
      </section>

      <section className="stack-sm">
        <TituloSeccion>Registros recientes</TituloSeccion>
        {datos.diario.map((r) => (
          <div key={r.id} className="card" style={{ padding: '16px 18px' }}>
            <div className="between">
              <span className="muted small">{formatearFecha(r.fecha)}</span>
              <button className={r.permitirIA ? 'chip on' : 'chip'} aria-pressed={r.permitirIA} onClick={() => { actualizar('diario', r.id, { permitirIA: !r.permitirIA }); setPatrones('inactivo'); }}>
                {r.permitirIA ? 'IA permitida' : 'Privado · sin IA'}
              </button>
            </div>
            <p style={{ margin: '8px 0 0', fontWeight: 600 }}>
              {r.emociones.length ? r.emociones.join(' · ') : 'Sin emoción elegida'}{' '}
              <span className="muted small" style={{ fontWeight: 400 }}>{r.intensidad ? 'Intensidad ' + r.intensidad + '/5' : ''}</span>
            </p>
            {r.intencion ? <p className="small" style={{ margin: '4px 0 0' }}>Intención: {r.intencion}</p> : null}
            {r.queNecesito ? <p className="small muted" style={{ margin: '4px 0 0' }}>Necesito: {r.queNecesito}</p> : null}
          </div>
        ))}
      </section>
    </div>
  );
}
